
import React, { useState } from "react";
import {
    Box,
    Button,
    TextField,
    Typography,
    Paper,
    Alert,
    InputAdornment,
    IconButton,
    CircularProgress,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import api from "../../services/api_client";

const FirstLoginPassword = () => {
    const [form, setForm] = useState({
        current_password: "",
        new_password: "",
        confirm_password: "",
    });

    const [showPasswords, setShowPasswords] = useState({
        current: false,
        new: false,
        confirm: false,
    });

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");

    const handleChange = (e) => {
        const { name, value } = e.target;

        setForm((prev) => ({
            ...prev,
            [name]: value,
        }));

        setError("");
    };

    const validatePassword = () => {
        const password = form.new_password;

        if (!form.current_password) {
            return "Veuillez saisir votre mot de passe temporaire actuel.";
        }

        if (password.length < 8) {
            return "Le mot de passe doit contenir au moins 8 caractères.";
        }

        if (new TextEncoder().encode(password).length > 72) {
            return "Le mot de passe ne doit pas dépasser 72 octets.";
        }

        if (!/[A-Z]/.test(password)) {
            return "Ajoutez au moins une lettre majuscule.";
        }

        if (!/[a-z]/.test(password)) {
            return "Ajoutez au moins une lettre minuscule.";
        }

        if (!/[0-9]/.test(password)) {
            return "Ajoutez au moins un chiffre.";
        }

        if (password === form.current_password) {
            return "Le nouveau mot de passe doit être différent de l'ancien.";
        }

        if (!form.confirm_password) {
            return "Veuillez confirmer votre nouveau mot de passe.";
        }

        if (password !== form.confirm_password) {
            return "Les deux mots de passe ne correspondent pas.";
        }

        return null;
    };

    const toggleVisibility = (field) => {
        setShowPasswords((prev) => ({
            ...prev,
            [field]: !prev[field],
        }));
    };

    // Suppression des clés réellement utilisées par api_client.js
    const clearSession = () => {
        const keys = [
            "smarthr_token",
            "smarthr_refresh_token",
            "smarthr_user",
            "access_token",
            "refresh_token",
            "token",
            "user",
            "currentUser",
        ];

        keys.forEach((key) => {
            localStorage.removeItem(key);
            sessionStorage.removeItem(key);
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (loading || success) return;

        setError("");
        setSuccess("");

        const validationError = validatePassword();

        if (validationError) {
            setError(validationError);
            return;
        }

        setLoading(true);

        try {
            const response = await api.post(
                "/auth/change-first-password",
                {
                    current_password: form.current_password,
                    new_password: form.new_password,
                }
            );

            if (response.data?.must_change_password !== false) {
                throw new Error(
                    "Le backend n'a pas confirmé la modification du mot de passe."
                );
            }

            setSuccess(
                "Mot de passe modifié avec succès. Vous allez être redirigé vers la connexion."
            );

            clearSession();

            setForm({
                current_password: "",
                new_password: "",
                confirm_password: "",
            });

            // Recharge complètement l'application sans ancienne session.
            window.setTimeout(() => {
                window.location.replace("/login");
            }, 2000);

        } catch (err) {
            console.error(
                "Erreur changement mot de passe :",
                err.response?.data || err
            );

            setError(
                err.response?.data?.detail ||
                err.message ||
                "Erreur lors du changement du mot de passe."
            );

        } finally {
            setLoading(false);
        }
    };

    const passwordField = (name, label, field) => (
        <TextField
            fullWidth
            margin="normal"
            name={name}
            label={label}
            type={showPasswords[field] ? "text" : "password"}
            value={form[name]}
            onChange={handleChange}
            required
            disabled={loading || Boolean(success)}
            autoComplete={
                field === "current"
                    ? "current-password"
                    : "new-password"
            }
            inputProps={{ maxLength: 72 }}
            InputProps={{
                endAdornment: (
                    <InputAdornment position="end">
                        <IconButton
                            onClick={() => toggleVisibility(field)}
                            edge="end"
                            disabled={loading}
                            aria-label="Afficher ou masquer le mot de passe"
                        >
                            {showPasswords[field]
                                ? <VisibilityOff />
                                : <Visibility />}
                        </IconButton>
                    </InputAdornment>
                ),
            }}
        />
    );

    return (
        <Box
            sx={{
                minHeight: "100vh",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: "#f5f7fb",
                p: 2,
            }}
        >
            <Paper
                elevation={4}
                sx={{
                    width: "100%",
                    maxWidth: 480,
                    p: 4,
                    borderRadius: 3,
                }}
            >
                <Typography
                    variant="h5"
                    fontWeight="bold"
                    mb={1}
                    textAlign="center"
                >
                    Changement obligatoire du mot de passe
                </Typography>

                <Typography
                    color="text.secondary"
                    mb={3}
                    textAlign="center"
                >
                    Pour sécuriser votre compte, vous devez remplacer
                    votre mot de passe temporaire avant de continuer.
                </Typography>

                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                {success && (
                    <Alert severity="success" sx={{ mb: 2 }}>
                        {success}
                    </Alert>
                )}

                <Box
                    component="form"
                    onSubmit={handleSubmit}
                    autoComplete="on"
                >
                    {passwordField(
                        "current_password",
                        "Mot de passe temporaire actuel",
                        "current"
                    )}

                    {passwordField(
                        "new_password",
                        "Nouveau mot de passe",
                        "new"
                    )}

                    {passwordField(
                        "confirm_password",
                        "Confirmer le nouveau mot de passe",
                        "confirm"
                    )}

                    <Typography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                        mt={2}
                    >
                        Votre nouveau mot de passe doit contenir :
                    </Typography>

                    <Box sx={{ mt: 1, ml: 1 }}>
                        <Typography variant="caption" display="block">
                            • Au moins 8 caractères
                        </Typography>
                        <Typography variant="caption" display="block">
                            • Une lettre majuscule
                        </Typography>
                        <Typography variant="caption" display="block">
                            • Une lettre minuscule
                        </Typography>
                        <Typography variant="caption" display="block">
                            • Au moins un chiffre
                        </Typography>
                        <Typography variant="caption" display="block">
                            • Maximum 72 octets
                        </Typography>
                    </Box>

                    <Button
                        type="submit"
                        fullWidth
                        variant="contained"
                        disabled={loading || Boolean(success)}
                        sx={{
                            mt: 3,
                            py: 1.4,
                            borderRadius: 2,
                            fontWeight: "bold",
                        }}
                    >
                        {loading ? (
                            <>
                                <CircularProgress
                                    size={20}
                                    color="inherit"
                                    sx={{ mr: 1 }}
                                />
                                Modification en cours...
                            </>
                        ) : (
                            "Modifier mon mot de passe"
                        )}
                    </Button>
                </Box>
            </Paper>
        </Box>
    );
};

export default FirstLoginPassword;
